import React, { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { Alert, Box, Button, FormHelperText, MenuItem, TextField, Typography } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import OrgsDropdown from "../../../components/orgsDropdown/OrgsDropdown"
import GroupsDropdown from "../../../components/groupsDropdown/GroupsDropdown"
import LtsAppStatusDropdown from "../../../components/ltsAppStatusDropdown/LtsAppStatusDropdown"
import AppIDDropdown from "../../../components/appIDDropdown/AppIDDropdown"
import { isFilterReady } from "../../../components/filterByDropdown/filterValidation"
import api from "../../../../base/services/ResApi"
import TerminalReportTable from "./TerminalReportTable"
import { terminalViews, terminalReportHeaders } from "./terminalReportData"

export default function TerminalReport() {
  const location = useLocation()
  const [view, setView] = useState("approved")
  const [filter, setFilter] = useState("F_ORG_CODE")
  const [orgCode, setOrgCode] = useState("")
  const [groupId, setGroupId] = useState("")
  const [serial, setSerial] = useState("")
  const [status, setStatus] = useState("")
  const [applicationId, setApplicationId] = useState("")
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [criteria, setCriteria] = useState("")
  const request = useRef(0)
  useEffect(() => () => { request.current += 1 }, [])
  const resetResults = () => { request.current += 1; setLoading(false); setRows([]); setCriteria(""); setError("") }
  useEffect(() => {
    if (location.state) {
      request.current += 1
      setRows([]); setCriteria(""); setLoading(false)
      setOrgCode(location.state.orgCode || ""); setGroupId(location.state.groupId || "")
      setApplicationId("")
      setFilter(location.state.filterByType || "F_ORG_CODE")
    }
  }, [location.state])
  const search = async event => {
    event.preventDefault()
    let headers
    try { headers = terminalReportHeaders({ view, filter, orgCode, groupId, serial, status, applicationId }) }
    catch (err) { setError(err.message); return }
    const id = ++request.current
    setLoading(true); setError(""); setRows([]); setCriteria("")
    try {
      const response = await api.get("/terminals", { headers })
      if (id !== request.current) return
      if (Number(response.data?.code) >= 400) throw new Error(response.data.description)
      setRows(response.data?.dataList || [])
      setCriteria(`${terminalViews[view]} · ${headers.Filter === "F_ORG_CODE" ? "Organization" : headers.Filter === "F_GRP_ID" ? "Group" : "Serial"}: ${headers.Value}${status && view === "approved" ? ` · ${status} · App ID: ${applicationId.trim()}` : ""}`)
    } catch (err) {
      if (id === request.current) setError(err.response?.data?.description || err.message || "Unable to load the report. Please try again.")
    } finally { if (id === request.current) setLoading(false) }
  }
  return <Box>
    <Box component="form" onSubmit={search} sx={{ mb: 3 }}>
      <Box className="report-filters" sx={{ display: "grid", gap: 3,
        "& .MuiFormControl-root": { minWidth: 0, width: "100%" } }}>
        <TextField select label="Terminal view" value={view} onChange={e => { resetResults(); setView(e.target.value); setStatus(""); setApplicationId(""); if (e.target.value === "unassigned") { setFilter("F_ORG_CODE"); setGroupId("") } }}>
          {Object.entries(terminalViews).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <TextField select label="Filter by" value={filter} disabled={view === "unassigned"} onChange={e => { resetResults(); setFilter(e.target.value); setGroupId(""); setSerial("") }}>
          <MenuItem value="F_ORG_CODE">Organization</MenuItem><MenuItem value="F_GRP_ID">Group</MenuItem><MenuItem value="F_TRMNL_SERIAL">Terminal serial</MenuItem>
        </TextField>
        {filter !== "F_TRMNL_SERIAL" && <Box sx={{ minHeight: 60 }}><OrgsDropdown orgCode={orgCode} onChange={e => { resetResults(); setOrgCode(e?.value || ""); setGroupId(""); setApplicationId("") }} /></Box>}
        {filter === "F_GRP_ID" && <Box sx={{ minHeight: 60 }}><GroupsDropdown key={orgCode} orgCode={orgCode} groupId={groupId} isGrpDisabled={!orgCode}
          onChange={e => { resetResults(); setGroupId(e?.value || "") }} /></Box>}
        {filter === "F_TRMNL_SERIAL" && <TextField label="Terminal serial" value={serial} onChange={e => { resetResults(); setSerial(e.target.value) }} />}
        {view === "approved" && <Box sx={{ minHeight: 60 }}><LtsAppStatusDropdown value={status} onChange={e => { resetResults(); setStatus(e?.value || ""); setApplicationId("") }} /></Box>}
        {view === "approved" && status && <Box><AppIDDropdown key={orgCode || "all-orgs"} orgCode={orgCode} value={applicationId}
          onChange={app => { resetResults(); setApplicationId(String(app?.applicationId ?? "")) }} />
          <FormHelperText>Required for the selected application status.</FormHelperText></Box>}
      </Box>
      {view === "unassigned" && <Alert severity="info" sx={{ mt: 2 }}>Unassigned terminals can only be filtered by organization.</Alert>}
      {view === "bulk" && <Alert severity="info" sx={{ mt: 2 }}>Bulk-imported terminals waiting to register. This is separate from pending approval.</Alert>}
      <Box className="report-actions" sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={loading || !isFilterReady(view === "unassigned" ? "F_ORG_CODE" : filter, orgCode, groupId, serial)}>{loading ? "Loading…" : "Search"}</Button>
        <Button variant="outlined" onClick={() => { resetResults(); setOrgCode(""); setGroupId(""); setSerial(""); setFilter("F_ORG_CODE"); setStatus(""); setApplicationId("") }}>Clear</Button>
      </Box>
    </Box>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {criteria && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{criteria}</Typography>}
    <TerminalReportTable terminalReportData={rows} loading={loading} />
  </Box>
}
